import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';

export interface FreeItemReward {
  id: string;
  threshold: number; // Order value threshold in USD
  productId: string | null;
  productName: string | null;
  productImage: string | null;
  milestoneLabel: string | null; // Label for milestone-only rewards (when no product linked)
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Get all free item rewards for admin management
 */
export const getAllFreeItemRewards = async (): Promise<FreeItemReward[]> => {
  try {
    const rewardsCollection = collection(db, 'freeItemRewards');
    const rewardsQuery = query(rewardsCollection, orderBy('threshold', 'asc'));
    
    const snapshot = await getDocs(rewardsQuery);
    
    const rewards = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as FreeItemReward[];
    
    return rewards;
  } catch (error) {
    console.error('Error fetching all free item rewards:', error);
    return [];
  }
};

/**
 * Create a new free item reward
 */
export const createFreeItemReward = async (
  threshold: number,
  productId: string | null,
  milestoneLabel?: string | null
): Promise<string> => {
  try {
    const rewardsCollection = collection(db, 'freeItemRewards');
    
    let newReward: any = {
      threshold,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Only add product details if productId is provided
    if (productId) {
      // Fetch product details from the products collection
      const productDoc = doc(db, 'products', productId);
      const productSnapshot = await getDoc(productDoc);
      
      if (!productSnapshot.exists()) {
        throw new Error('Product not found');
      }
      
      const productData = productSnapshot.data();
      newReward.productId = productId;
      newReward.productName = productData.name;
      newReward.productImage = productData.image;
      newReward.milestoneLabel = null; // Clear milestone label when product is linked
    } else {
      // Set to null for thresholds without linked products
      newReward.productId = null;
      newReward.productName = null;
      newReward.productImage = null;
      newReward.milestoneLabel = milestoneLabel || null; // Set milestone label for milestone-only rewards
    }
    
    const docRef = await addDoc(rewardsCollection, newReward);
    return docRef.id;
  } catch (error) {
    console.error('Error creating free item reward:', error);
    throw new Error('Failed to create free item reward');
  }
};

/**
 * Update a free item reward
 */
export const updateFreeItemReward = async (
  rewardId: string,
  updates: Partial<Omit<FreeItemReward, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> => {
  try {
    const rewardDoc = doc(db, 'freeItemRewards', rewardId);
    
    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(rewardDoc, updateData);
  } catch (error) {
    console.error('Error updating free item reward:', error);
    throw new Error('Failed to update free item reward');
  }
};

/**
 * Delete a free item reward
 */
export const deleteFreeItemReward = async (rewardId: string): Promise<void> => {
  try {
    const rewardDoc = doc(db, 'freeItemRewards', rewardId);
    await deleteDoc(rewardDoc);
  } catch (error) {
    console.error('Error deleting free item reward:', error);
    throw new Error('Failed to delete free item reward');
  }
};

/**
 * Toggle reward active status
 */
export const toggleFreeItemReward = async (rewardId: string, isActive: boolean): Promise<void> => {
  try {
    await updateFreeItemReward(rewardId, { isActive });
  } catch (error) {
    console.error('Error toggling free item reward:', error);
    throw new Error('Failed to toggle free item reward');
  }
};